import { useCallback, useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router';
import { BotaoTema } from './BotaoTema';
import { MenuCelular } from './MenuCelular';
import { LogoMPC, NomeDaMarca } from './LogoMPC';
import { NotificationCenter } from './NotificationCenter';
import { SaldoNoCabecalho } from './MoedaMpc';
import { BotaoLink } from './ui/Botao';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/cn';

/**
 * Classificação e artilharia não estão aqui de propósito: os dois são dados de
 * uma edição, e vivem nas abas do próprio campeonato. No topo eles obrigavam a
 * escolher a edição num seletor antes de mostrar qualquer coisa — um passo a
 * mais para chegar onde `/campeonatos` já leva.
 *
 * Rankings entra no topo pelo motivo oposto: abre somando todas as edições, sem
 * pedir escolha nenhuma, e é a página a que quem joga a MPC volta para ver onde
 * está.
 */
const NAV = [
  { to: '/', label: 'Início', end: true },
  { to: '/campeonatos', label: 'Campeonatos' },
  { to: '/rankings', label: 'Rankings' },
  { to: '/times', label: 'Times' },
  { to: '/loja', label: 'Loja' },
] as const;

export function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const fechar = useCallback(() => setOpen(false), []);
  const { isStaff } = useAuth();

  // O cabeçalho começa transparente sobre a hero e ganha fundo ao rolar —
  // sem isso, a barra corta a composição logo no primeiro pixel.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={cn(
        'fixed inset-x-0 top-0 z-50 transition-[padding,background-color,border-color] duration-[--duration-standard] ease-[--ease-signature]',
        /*
          A navbar do template: logo à esquerda, os links numa pílula no meio e
          os botões à direita, flutuando a 1.5rem do topo sobre o brilho da
          página. O original é `position: absolute` e some ao rolar; aqui ela é
          fixa, porque numa classificação ou no painel o menu precisa continuar à
          mão. Ao rolar, a barra ganha fundo e encolhe o respiro de cima.

          Sem `backdrop-blur` no fundo da barra, e essa é a maior causa do
          travamento na rolagem: um cabeçalho fixo com desfoque obriga o
          navegador a reler e borrar tudo o que passa por trás dele a cada
          quadro, durante a rolagem inteira. Fundo quase opaco entrega a mesma
          leitura.
        */
        scrolled
          ? 'border-b border-line bg-ink/92 py-3'
          : 'border-b border-transparent bg-transparent py-4 sm:py-6',
      )}
    >
      <div className="mx-auto flex max-w-mpc items-center justify-between gap-6 px-6">
        <Link
          to="/"
          className="flex shrink-0 items-center transition-opacity duration-[--duration-quick] hover:opacity-80"
          aria-label="MPC — página inicial"
        >
          {/* 3.25rem, a mesma altura do `.nav-logo` na home. */}
          <LogoMPC altura={52} />
          <NomeDaMarca />
        </Link>

        {/* A pílula dos links: `.nav-menu-items` do template — fundo grafite
            translúcido, raio de 3.125rem, links de peso médio na caixa normal,
            e o ativo pintado de limão em vez de sublinhado. */}
        <nav
          className="hidden items-center gap-6 rounded-full bg-(--pilula-menu) px-8 py-4 shadow-(--sombra-pilula) lg:flex xl:gap-[1.88rem] xl:px-10 xl:py-5"
          aria-label="Principal"
        >
          {linksDoMenu(isStaff).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'text-base leading-none font-medium transition-colors duration-[--duration-quick] xl:text-lg',
                  isActive ? 'text-brand-ink' : 'text-paper hover:text-brand-ink',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex shrink-0 items-center gap-2.5">
          <BotaoTema className="hidden sm:inline-flex" />
          <SessionArea />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="menu-celular"
            aria-label={open ? 'Fechar menu' : 'Abrir menu'}
            className="group relative size-11 rounded-full bg-(--vidro) text-paper transition-colors hover:text-brand-ink lg:hidden"
          >
            {/*
              Três traços que viram X, em vez de trocar um ícone pelo outro.

              A troca de `d` no `<path>` não anima — o ícone pula de hambúrguer
              para X num quadro. Com três `<span>` posicionados, o gesto vira
              rotação e opacidade: o traço do meio some e os de fora se cruzam.
            */}
            <span aria-hidden className="absolute inset-0 flex flex-col items-center justify-center gap-1.25">
              <span
                className={cn(
                  'block h-0.5 w-5 rounded-full bg-current transition-transform duration-[--duration-standard] ease-[--ease-signature]',
                  open && 'translate-y-1.75 rotate-45',
                )}
              />
              <span
                className={cn(
                  'block h-0.5 w-5 rounded-full bg-current transition-opacity duration-[--duration-quick]',
                  open && 'opacity-0',
                )}
              />
              <span
                className={cn(
                  'block h-0.5 w-5 rounded-full bg-current transition-transform duration-[--duration-standard] ease-[--ease-signature]',
                  open && '-translate-y-1.75 -rotate-45',
                )}
              />
            </span>
          </button>
        </div>
      </div>

      <MenuCelular aberto={open} onFechar={fechar} />
    </header>
  );
}

/**
 * Os links do menu. A central de comando entra no fim para quem é da
 * organização: quem administra a competição não deveria caçar o painel dentro
 * de um menu de avatar.
 */
function linksDoMenu(isStaff: boolean) {
  return [
    ...NAV.map((item) => ({ to: item.to, label: item.label, end: 'end' in item ? item.end : false })),
    ...(isStaff ? [{ to: '/admin', label: 'Painel', end: true }] : []),
  ];
}

/**
 * Canto direito do cabeçalho.
 *
 * Visitante vê "Entrar" e "Criar conta" (no celular, os dois ficam no menu). Capitão logado vê o atalho para o
 * time dele — que é o lugar para onde ele volta toda vez. Quem é da organização
 * ganha também o link do painel, sem precisar decorar a URL.
 */
export function SessionArea() {
  const { user, teams, isStaff, isAdmin, loading, logout } = useAuth();
  const [open, setOpen] = useState(false);

  if (loading) return <div className="h-12 w-36 animate-pulse rounded-full bg-surface-2" aria-hidden />;

  if (!user) {
    return (
      <>
        {/* Os dois botões do template: contorno claro e o branco, este com o
            limão subindo no hover — o "Inscrever time" do menu da home. */}
        <BotaoLink to="/entrar" variante="contorno" tamanho="md" className="hidden sm:inline-flex">
          Entrar
        </BotaoLink>
        <BotaoLink to="/cadastrar-time" variante="claro" tamanho="md" className="hidden sm:inline-flex">
          Criar conta
        </BotaoLink>
      </>
    );
  }

  const team = teams[0];

  return (
    <div className="flex items-center gap-2">
      <SaldoNoCabecalho />
      <NotificationCenter />

      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex items-center gap-2.5 rounded-full bg-(--vidro) py-2 pr-4 pl-2 text-base font-medium transition-colors hover:bg-brand hover:text-black"
        >
        {user.avatarUrl ? (
          <img src={user.avatarUrl} alt="" className="size-8 rounded-full" />
        ) : (
          <span className="flex size-8 items-center justify-center rounded-full bg-brand text-xs font-bold text-black">
            {user.displayName.slice(0, 2).toUpperCase()}
          </span>
        )}
        <span className="hidden max-w-28 truncate sm:inline">{user.displayName}</span>
      </button>

      {open && (
        <>
          {/* Camada invisível que fecha o menu ao clicar fora, sem listener global. */}
          <button
            type="button"
            aria-label="Fechar menu"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 cursor-default"
          />
          <div className="mpc-panel absolute right-0 z-50 mt-2 w-56 overflow-hidden py-1 shadow-[var(--shadow-lift)]">
            <p className="truncate border-b border-line px-4 py-2.5 text-xs text-faint">
              {user.email ?? user.displayName}
            </p>

            {/* O nome do time abre o perfil dele, como qualquer visitante vê;
                a edição fica no botão "Editar clube" do próprio perfil. */}
            {team ? (
              <Link
                to={`/times/${team.slug}`}
                onClick={() => setOpen(false)}
                className="block px-4 py-2.5 text-sm text-muted transition-colors hover:bg-surface-2 hover:text-paper"
              >
                {team.name}
              </Link>
            ) : (
              <Link
                to="/meu-time/novo"
                onClick={() => setOpen(false)}
                className="block px-4 py-2.5 text-sm text-brand-ink transition-colors hover:bg-surface-2"
              >
                Cadastrar meu time
              </Link>
            )}

            {/* Bloco da organização.
                Só aparece para quem tem papel `admin` ou `staff` — e não é só
                a tela que esconde: cada rota de `/api/admin/*` exige o papel no
                servidor. Esconder o link é conveniência; a permissão de verdade
                mora na API, onde ninguém contorna abrindo o endereço na mão. */}
            {isStaff && (
              <>
                <p className="border-t border-line px-4 pt-3 pb-1 text-xs font-medium text-faint">
                  Organização
                </p>
                {[
                  { to: '/admin', label: 'Central de comando' },
                  { to: '/admin/campeonatos', label: 'Campeonatos' },
                  { to: '/admin/tickets', label: 'Tickets' },
                  { to: '/admin/moedas', label: 'Moedas & Loja' },
                  // Só admin: o coletor é infraestrutura da organização, e a
                  // listagem devolve o IP residencial de quem hospeda o agente.
                  ...(isAdmin
                    ? [
                        { to: '/admin/usuarios', label: 'Usuários & Permissões' },
                        { to: '/admin/coletor', label: 'Agentes do coletor' },
                      ]
                    : []),
                ].map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setOpen(false)}
                    className="block px-4 py-2 text-sm text-muted transition-colors hover:bg-surface-2 hover:text-brand-ink"
                  >
                    {item.label}
                  </Link>
                ))}
              </>
            )}

            <button
              type="button"
              onClick={() => {
                setOpen(false);
                logout();
              }}
              className="block w-full border-t border-line px-4 py-2.5 text-left text-sm text-muted transition-colors hover:bg-surface-2 hover:text-loss"
            >
              Sair
            </button>
          </div>
        </>
      )}
      </div>
    </div>
  );
}
